import { HtmlHTMLAttributes, memo } from "react";
import classNames from "classnames";
import { formatBytes } from "../../../utils";
import { CircularProgress } from "../../ui/Progress";

import { ReactComponent as MinimizeIcon } from "./images/icon.svg";

import styles from "./style.module.scss";

export type DownloadProgressCardProps = {
  finished?: boolean,
  fileName: string,
  size: number,
  downloaded: number,
  onMinimizeClick?: () => void
} & HtmlHTMLAttributes<HTMLDivElement>

export const DownloadProgressCard = memo<DownloadProgressCardProps>((
  {
    className,
    finished,
    size,
    downloaded,
    fileName,
    onMinimizeClick,
    ...restProps
  }) => {
  return (
    <div
      className={classNames(className, styles.root, { [styles.finished]: finished })}
      onClick={() => {
        if (finished) {
          onMinimizeClick?.();
        }
      }}
      {...restProps}
    >
      <span className={styles.title}>
        {finished ? "The file had been downloaded" : "The file is being downloaded"}
      </span>
      {
        !finished &&
        <button
          className={styles.minimize}
          onClick={onMinimizeClick}
          title="Minimize"
        >
          <MinimizeIcon />
        </button>
      }
      <div className={styles.progressBlock}>
        <CircularProgress
          className={styles.progressRoot}
          backgroundClassName={styles.progressBackground}
          textClassName={classNames(styles.progressText, { [styles.progressTextFinished]: finished || downloaded === size })}
          size={66}
          strokeWidth={finished ? 2 : 4}
          value={(downloaded / size)}
          successIcon={
            <g transform="translate(16,16)">
              <path
                d="M17.17 15.6a.68.68 0 0 0-.68.68v10.61l-3.334-3.42a.63.63 0 0 0-.919 0c-.265.265-.244.696.021.961l4.317 4.307a.692.692 0 0 0 .97 0l4.308-4.307a.68.68 0 0 0 0-.962c-.265-.265-.78-.265-1.045 0l-2.958 2.873V16.28a.68.68 0 0 0-.68-.68z"
              />
              <path
                d="M30.708 16.038c0-3.317-2.725-5.983-6.075-5.983-.402 0-.782.076-1.14.16-1.199-2.61-3.844-4.316-6.765-4.316a7.435 7.435 0 0 0-7.343 6.246 5.11 5.11 0 0 0-.85-.069c-2.866 0-5.11 2.128-5.11 4.927 0 2.798 2.245 4.927 5.11 4.927h5.405a.68.68 0 0 0 0-1.36H8.535c-2.103 0-3.749-1.53-3.749-3.566 0-2.036 1.647-3.599 3.749-3.599.38 0 .754.073 1.107.183l.129.05a.677.677 0 0 0 .886-.608 6.07 6.07 0 0 1 6.07-5.764c2.56 0 4.858 1.608 5.72 4a.717.717 0 0 0 .53.44c.108.021.22.016.326-.015.49-.147.904-.244 1.33-.244 2.6 0 4.715 2.024 4.715 4.591 0 2.578-2.07 4.532-4.715 4.532H20.4a.68.68 0 0 0 0 1.36h4.233c3.407 0 6.075-2.552 6.075-5.892z"
              />
            </g>
          }
        />
      </div>
      <div className={styles.contentBlock}>
        <div className={styles.downloaded}>{formatBytes(downloaded)}</div>
        <div className={styles.size}>{!finished ? "of " : ""}{formatBytes(size)} loaded</div>
        <div className={styles.fileName}>{fileName}</div>
      </div>
    </div>
  );
});

export default DownloadProgressCard;